import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('CasketUrnData');

import {
  CasketUrnInfoRec,
  casketUrnInfoService,
  CasketDetails,
  UrnDetails,
  OtherDetails,
} from '../../../../services/casketUrnInfo';

type RouteParams = {
  casketUrnId?: number;
  taskId?: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type CasketUrnDataScreenRouteProp = RouteProp<RootStackParamList, 'CasketUrnData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const CasketUrnDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<CasketUrnDataScreenRouteProp>();
  const [casketUrnInfo, setCasketUrnInfo] = useState<CasketUrnInfoRec | null>(null);
  const [loading, setLoading] = useState(true);

  const casketUrnId = route.params?.casketUrnId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadCasketUrnInfo();
  }, []);

  const loadCasketUrnInfo = async () => {
    try {
      if (casketUrnId) {
        const info = await casketUrnInfoService.getCasketUrnInfoById(casketUrnId, targetUserId);
        setCasketUrnInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await casketUrnInfoService.getAllCasketUrnInfo(targetUserId);
        if (allInfo.length > 0) {
          setCasketUrnInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading casket/urn info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (casketUrnInfo) {
      // Navigate directly to the appropriate input screen based on containerType
      const containerType = casketUrnInfo.containerType;
      const editParams = {
        casketUrnData: casketUrnInfo,
        isEditing: true
      };

      switch (containerType) {
        case 'casket':
          (navigation as any).navigate('CasketUrnInputCasket', editParams);
          break;
        case 'urn':
          (navigation as any).navigate('CasketUrnInputUrn', editParams);
          break;
        case 'other':
          (navigation as any).navigate('CasketUrnInputOther', editParams);
          break;
        default:
          // Fallback to intro screen if containerType is unknown
          navigation.navigate('CasketUrnInput');
      }
    } else {
      // No existing data, go to intro screen to select type
      navigation.navigate('CasketUrnInput');
    }
  };

  const handleDelete = () => {
    if (casketUrnInfo) {
      Alert.alert(
        'Delete Casket/Urn Information',
        'Are you sure you want to delete this casket/urn information? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await casketUrnInfoService.deleteCasketUrnInfo(casketUrnInfo.id);
                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting casket/urn info:', error);
                Alert.alert('Error', 'Failed to delete casket/urn information');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  const renderContainerDetails = () => {
    if (!casketUrnInfo || !casketUrnInfo.details) {
      return null;
    }

    const details = casketUrnInfo.details;
    const containerType = casketUrnInfo.containerType;

    switch (containerType) {
      case 'casket':
        const casketDetails = details as CasketDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Casket Details</Text>
            <InfoItem label="Material" value={casketDetails.material} />
            <InfoItem label="Color" value={casketDetails.color} />
            <InfoItem label="Style" value={casketDetails.style} />
            <InfoItem label="Personalization" value={casketDetails.personalization} />
            <InfoItem label="Custom Made" value={casketDetails.customMade} />
            <InfoItem label="Specifications" value={casketDetails.specifications} />
          </View>
        );

      case 'urn':
        const urnDetails = details as UrnDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Urn Details</Text>
            <InfoItem label="Material" value={urnDetails.material} />
            <InfoItem label="Size" value={urnDetails.size} />
            <InfoItem label="Color" value={urnDetails.color} />
            <InfoItem label="Style" value={urnDetails.style} />
            <InfoItem label="Personalization" value={urnDetails.personalization} />
            <InfoItem label="Biodegradable" value={urnDetails.biodegradable} />
            <InfoItem label="Keepsake Urns" value={urnDetails.keepsakeUrns} />
          </View>
        );

      case 'other':
        const otherDetails = details as OtherDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Other Container Details</Text>
            <InfoItem label="Description" value={otherDetails.description} />
            <InfoItem label="Material" value={otherDetails.material} />
            <InfoItem label="Size" value={otherDetails.size} />
            <InfoItem label="Special Features" value={otherDetails.specialFeatures} />
          </View>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Casket/Urn Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Casket/Urn Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {casketUrnInfo ? (
            <>
              <InfoItem
                label="Container Type"
                value={casketUrnInfo.containerType ?
                  casketUrnInfo.containerType.charAt(0).toUpperCase() +
                  casketUrnInfo.containerType.slice(1) : 'Not specified'}
              />

              <InfoItem
                label="Specific Type"
                value={casketUrnInfo.specificType ?
                  casketUrnInfo.specificType.charAt(0).toUpperCase() +
                  casketUrnInfo.specificType.slice(1) : 'Not specified'}
              />

              {casketUrnInfo.specialInstructions && (
                <InfoItem label="Special Instructions" value={casketUrnInfo.specialInstructions} />
              )}

              {casketUrnInfo.contactName && (
                <InfoItem label="Contact Name" value={casketUrnInfo.contactName} />
              )}

              {casketUrnInfo.contactPhone && (
                <InfoItem label="Contact Phone" value={casketUrnInfo.contactPhone} />
              )}

              {casketUrnInfo.estimatedCost && (
                <InfoItem label="Estimated Cost" value={`$${casketUrnInfo.estimatedCost.toLocaleString()}`} />
              )}

              {casketUrnInfo.purchaseLocation && (
                <InfoItem label="Purchase Location" value={casketUrnInfo.purchaseLocation} />
              )}

              {renderContainerDetails()}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No casket/urn information found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Casket/Urn Information</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(20),
    marginBottom: ms(15),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default CasketUrnDataScreen;